import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HD_BODYGRAPH_COLORS } from '../../bodygraph/hd-bodygraph-metadata';
import { HdBodygraphPlanetaryActivation } from '../../bodygraph/hd-bodygraph.models';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { getHdPlanetDisplayConfigByPlanetId } from '../../bodygraph/planet-grid/hd-planet-display-config';
import {
  HD_JUST_NOW_TRANSIT_BASE_COLOR,
  HD_JUST_NOW_TRANSIT_COLOR,
} from '../just-now-transit/hd-just-now-transit';
import {
  HdLocksKeysScope,
  HdLocksKeysStarGroup,
  getHdLocksKeysPeriodMeta,
  hdLocksKeysKeyGate,
} from './hd-locks-keys-metadata';
import { hdLocksKeysPeriodText } from './hd-locks-keys-period-text';
import { HdLocksKeysPeriodPairToggleComponent } from './hd-locks-keys-period-pair-toggle.component';
import {
  HdLocksKeysRelationshipRowVm,
  buildHdLocksKeysRelationshipRows,
} from './hd-locks-keys-relationship';
import { HdLocksKeysChartSearchComponent } from './hd-locks-keys-chart-search.component';
import { HdLocksKeysStateService } from './hd-locks-keys-state.service';
import { HdLocksKeysOverlayToggleComponent } from './hd-locks-keys-overlay-toggle.component';
import { openHdGlobalCyclesTablePanel } from './hd-global-cycles-table-panel.component';
import {
  HdLocksKeysIntroPanelComponent,
  HD_LOCKS_KEYS_INTRO_PANEL_KEY,
} from './hd-locks-keys-intro-panel.component';
import {
  HdLocksKeysLockDetailComponent,
  HdLocksKeysLockDetailData,
  hdLocksKeysLockDetailPanelKey,
} from './hd-locks-keys-lock-detail.component';

/** Astronomicon-planeetchip bij een geactiveerde key (kleur = chart-zijde). */
interface LocksKeysPlanetChipVm {
  readonly key: string;
  readonly glyph: string;
  readonly color: string;
}

interface LocksKeysPanelRowVm {
  readonly lockGate: number;
  readonly role: string;
  /** Kortere richting-/liefde-kwalificatie achter de rol (subtiele font). */
  readonly roleQualifier: string;
  readonly theme: string;
  /** Key-poort in de linker- en rechterperiode van het paar (tweeluik-toggle). */
  readonly pairKeyGates: readonly [number, number];
  /** Key-poort van de actieve periode (voor status-tooltips). */
  readonly activeKeyGate: number;
  readonly keyActivated: boolean;
  readonly keyPlanets: readonly LocksKeysPlanetChipVm[];
  /** Planeetglyphs op de lock-poort zélf (slotpersoon-bewijs, chart-scope). */
  readonly lockPlanets: readonly LocksKeysPlanetChipVm[];
}

interface LocksKeysPanelGroupVm {
  readonly key: HdLocksKeysStarGroup;
  readonly label: string;
  /** Incarnatiekruis dat de keys van deze groep in de actieve periode vormen. */
  readonly keyCrossName: string;
  /** De keys van deze groep vormen het vierkant van het lentepunt (felle tint op het wiel). */
  readonly keysAreEquinox: boolean;
  readonly rows: readonly LocksKeysPanelRowVm[];
}

/** Legende-item van de keys-ster: een vierkant per arm, het vierkant van het lentepunt eerst. */
interface LocksKeysKeysStarLegendVm {
  readonly equinox: boolean;
  readonly crossName: string;
}

/**
 * Slot-/sleutel-/beide-classificatie van de geladen chart (chart-scope): een
 * slotpersoon draagt een natale planeet op een van de 8 G-locks, een
 * sleutelpersoon op een van de 8 keys van de periode. In composite telt de unie
 * van beide charts (vandaar `together`).
 */
interface LocksKeysPersonBadgeVm {
  readonly kind: 'both' | 'key' | 'lock' | 'open';
  readonly label: string;
  readonly counts: string;
  readonly tooltip: string;
  readonly together: boolean;
}

/**
 * Gedockt Locks & Keys-infopaneel (spec 2026-08-27): scope- en periodekeuze,
 * de vergelijkweergave-toggle en de lock/keys-tabel per incarnatiekruis. Leeft
 * — net als het transits-paneel — in de rechter dock-area; alle staat loopt
 * via {@link HdLocksKeysStateService} zodat mandala, paneel en detail dezelfde
 * bron lezen.
 */
@Component({
  selector: 'hd-locks-keys-panel',
  imports: [SharedModule, ESegmentedToggleComponent, HdLocksKeysOverlayToggleComponent, HdLocksKeysPeriodPairToggleComponent, HdLocksKeysChartSearchComponent],
  templateUrl: './hd-locks-keys-panel.component.html',
  styleUrl: './hd-locks-keys-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLocksKeysPanelComponent {
  protected readonly state = inject(HdLocksKeysStateService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly floatingPanels = inject(FloatingPanelService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected t(key: string, params?: Record<string, unknown>): string {
    return this.gateInfo.locksKeysTextIn(key, this.lang(), params);
  }

  /** Knoppen Algemeen / Op chart (gedeelde e-segmented-toggle); "op chart" uit zonder chart. */
  protected readonly scopeOptions = computed<readonly ESegmentedToggleOption<HdLocksKeysScope>[]>(() => {
    const hasChart = this.state.hasChart();
    return [
      { value: 'general', label: this.t('panel.scopeGeneral') },
      {
        value: 'chart',
        label: this.t('panel.scopeChart'),
        disabled: !hasChart,
        title: hasChart ? undefined : this.t('panel.scopeChartDisabledHint'),
      },
    ];
  });

  /** Teksten van de actieve periode (met terugval voor tijdperken zonder eigen content). */
  protected readonly activePeriodText = computed(() =>
    hdLocksKeysPeriodText(this.gateInfo, this.lang(), this.state.periodId()),
  );

  protected readonly showStatus = computed(() => this.state.effectiveScope() === 'chart');

  protected readonly groups = computed<readonly LocksKeysPanelGroupVm[]>(() => {
    const overview = this.state.overview();
    const planetary = this.state.chartPlanetaryActivations();
    const partnerPlanetary = this.state.partnerPlanetaryActivations();
    const hasPartner = this.state.hasPartner();
    const pair = this.state.periodPair();
    const chipsFor = (
      source: readonly HdBodygraphPlanetaryActivation[],
      prefix: string,
      gate: number,
      colorFor: (activation: HdBodygraphPlanetaryActivation) => string,
    ): LocksKeysPlanetChipVm[] =>
      source
        .filter(activation => activation.gate === gate)
        .map(activation => ({
          key: `${prefix}-${activation.id}`,
          glyph: getHdPlanetDisplayConfigByPlanetId(activation.planetId)?.astronomiconChar ?? '',
          color: colorFor(activation),
        }));
    const sideColor = (activation: HdBodygraphPlanetaryActivation): string =>
      activation.side === 'design' ? HD_BODYGRAPH_COLORS.design : HD_BODYGRAPH_COLORS.personality;
    const rows = overview.entries.map<LocksKeysPanelRowVm & { group: HdLocksKeysStarGroup }>(entry => {
      // Composite-tab: chips per persoon in de bestaande overlay-kleuren
      // (chart 1 blauw, chart 2 mint — zoals de glyphs op het wiel);
      // anders per zijde (design-rood / personality-zwart).
      const keyPlanets = hasPartner
        ? [
            ...chipsFor(planetary, 'base', entry.keyGate, () => HD_JUST_NOW_TRANSIT_BASE_COLOR),
            ...chipsFor(partnerPlanetary, 'partner', entry.keyGate, () => HD_JUST_NOW_TRANSIT_COLOR),
          ]
        : chipsFor(planetary, 'base', entry.keyGate, sideColor);
      // Planeetglyphs op de lock-poort zelf: maakt de "slotpersoon"-duiding
      // concreet (welke planeet op het slot staat), per persoon in composite.
      const lockPlanets = hasPartner
        ? [
            ...chipsFor(planetary, 'base-lock', entry.lock.gate, () => HD_JUST_NOW_TRANSIT_BASE_COLOR),
            ...chipsFor(partnerPlanetary, 'partner-lock', entry.lock.gate, () => HD_JUST_NOW_TRANSIT_COLOR),
          ]
        : chipsFor(planetary, 'base-lock', entry.lock.gate, sideColor);
      return {
        group: entry.lock.group,
        lockGate: entry.lock.gate,
        role: this.t(`locks.${entry.lock.gate}.role`),
        roleQualifier: this.t(`locks.${entry.lock.gate}.roleQualifier`),
        theme: this.t(`locks.${entry.lock.gate}.theme`),
        pairKeyGates: [
          hdLocksKeysKeyGate(entry.lock.gate, pair[0]),
          hdLocksKeysKeyGate(entry.lock.gate, pair[1]),
        ] as const,
        activeKeyGate: entry.keyGate,
        keyActivated: entry.keyActivated,
        keyPlanets,
        lockPlanets,
      };
    });
    const periodText = this.activePeriodText();
    const equinoxGroup = getHdLocksKeysPeriodMeta(this.state.periodId()).equinoxGroup;
    return (['vessel', 'sphinx'] as const).map(group => ({
      key: group,
      label: this.t(group === 'vessel' ? 'panel.groupVessel' : 'panel.groupSphinx'),
      keyCrossName: group === 'vessel' ? periodText.vesselCross : periodText.sphinxCross,
      keysAreEquinox: group === equinoxGroup,
      rows: rows.filter(row => row.group === group),
    }));
  });

  /**
   * Keys-ster in de legende, per arm zoals op het wiel: eerst het vierkant van
   * het lentepunt (fel), dan het andere (gedempt), elk met het kruis dat het in
   * de actieve periode vormt. Bij elke lock-fase wisselt het lock-kruis achter
   * het felle vierkant.
   */
  protected readonly keysStarLegend = computed<readonly LocksKeysKeysStarLegendVm[]>(() => {
    const periodText = this.activePeriodText();
    const equinoxIsVessel = getHdLocksKeysPeriodMeta(this.state.periodId()).equinoxGroup === 'vessel';
    return [
      { equinox: true, crossName: equinoxIsVessel ? periodText.vesselCross : periodText.sphinxCross },
      { equinox: false, crossName: equinoxIsVessel ? periodText.sphinxCross : periodText.vesselCross },
    ];
  });

  /**
   * Slot-/sleutel-/beide-classificatie van de geladen chart (chart-scope). In
   * de algemene scope `null` (er is geen chart om te duiden). In composite telt
   * de unie van beide charts mee, aangeduid met `together`.
   */
  protected readonly personBadge = computed<LocksKeysPersonBadgeVm | null>(() => {
    if (this.state.effectiveScope() !== 'chart') {
      return null;
    }
    const overview = this.state.overview();
    const lockCount = overview.entries.filter(entry => entry.lockActivations.length > 0).length;
    const keyCount = overview.activatedKeyCount;
    const kind: LocksKeysPersonBadgeVm['kind'] =
      keyCount > 0 && lockCount > 0 ? 'both'
        : keyCount > 0 ? 'key'
          : lockCount > 0 ? 'lock'
            : 'open';
    const labelKey =
      kind === 'both' ? 'panel.personBadgeBoth'
        : kind === 'key' ? 'panel.personBadgeKey'
          : kind === 'lock' ? 'panel.personBadgeLock'
            : 'panel.personBadgeOpen';
    return {
      kind,
      label: this.t(labelKey),
      counts: this.t('panel.personBadgeCounts', { locks: lockCount, keys: keyCount }),
      tooltip: this.t('panel.personBadgeTooltip'),
      together: this.state.hasPartner(),
    };
  });

  /**
   * Cross-persoon slot-sleutelverbindingen op een composite/relatie-tab: de
   * sleutel van de ene chart valt in het slot van de andere. Volgt de bovenaan
   * gekozen periode ({@link HdLocksKeysStateService.periodId}); chartnamen komen
   * uit de gespiegelde composite-namen. Leeg buiten composite.
   */
  protected readonly relationshipRows = computed<readonly HdLocksKeysRelationshipRowVm[]>(() => {
    if (!this.state.hasPartner()) {
      return [];
    }
    return buildHdLocksKeysRelationshipRows(
      this.gateInfo,
      this.lang(),
      this.state.periodId(),
      {
        name: this.state.chart1Name() || this.t('panel.relPerson1'),
        color: HD_JUST_NOW_TRANSIT_BASE_COLOR,
        activations: this.state.chartPlanetaryActivations(),
      },
      {
        name: this.state.chart2Name() || this.t('panel.relPerson2'),
        color: HD_JUST_NOW_TRANSIT_COLOR,
        activations: this.state.partnerPlanetaryActivations(),
      },
    );
  });

  protected toggleLocksStar(): void {
    this.state.showLocksStar.update(value => !value);
  }

  protected toggleKeysStar(): void {
    this.state.showKeysStar.update(value => !value);
  }

  protected keyStatusTooltip(row: LocksKeysPanelRowVm): string {
    return this.t(
      row.keyActivated ? 'panel.keyActivatedTooltip' : 'panel.keyNotActivatedTooltip',
      { gate: row.activeKeyGate },
    );
  }

  protected setScope(scope: HdLocksKeysScope): void {
    if (scope === 'chart' && !this.state.hasChart()) {
      return;
    }
    this.state.scope.set(scope);
  }

  protected toggleCompare(): void {
    this.state.compare.update(value => !value);
  }

  protected openIntro(): void {
    this.floatingPanels.openOrFocus<HdLocksKeysIntroPanelComponent, undefined>({
      key: HD_LOCKS_KEYS_INTRO_PANEL_KEY,
      component: HdLocksKeysIntroPanelComponent,
      data: undefined,
      dockTab: { label: this.t('title') },
      dockZone: 'full',
      closable: true,
      openInitiallyDocked: true,
      redockOnReopen: true,
    });
  }

  /** De Global Cycles-tabel rechtstreeks, zonder eerst het uitlegpaneel te openen. */
  protected openGlobalCyclesTable(): void {
    openHdGlobalCyclesTablePanel(this.floatingPanels);
  }

  protected openLockDetail(lockGate: number): void {
    this.floatingPanels.openOrFocus<HdLocksKeysLockDetailComponent, HdLocksKeysLockDetailData>({
      key: hdLocksKeysLockDetailPanelKey(lockGate),
      component: HdLocksKeysLockDetailComponent,
      data: { gate: lockGate },
      dockTab: { label: `Lock ${lockGate}` },
      dockZone: 'full',
      closable: true,
      openInitiallyDocked: true,
      redockOnReopen: true,
    });
  }
}
